import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchExhibitions } from 'src/api/exhibition'
import { useMasterData } from 'src/api/masterData'
import { useLoginUser } from 'src/api/user'
import { SearchIcon } from 'src/assets/svgs'
import { useRouteQuery, useRouteQueryArray } from 'src/hooks/useRouteQuery'
import { Pagination } from 'src/components/Pagination'
import { accentColor } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { ExhibitionCard } from './common'

export const ExhibitionSearchPageLayout = () => {
  const router = useRouter()
  const { user } = useLoginUser()
  const { data: masterData } = useMasterData()
  const { genre, tag, orderBy, filterBy, keyword } = router.query
  const [pageIndex, setPageIndex] = useState<number>(
    Number(router.query.page ?? '1'),
  )

  const [selectedTags, setSelectedTags] = useRouteQueryArray('tag', [], {
    resetPage: true,
  })
  const [keywordInput, setKeywordInput] = useRouteQuery('keyword', '', {
    debounce: 300,
    resetPage: true,
    serialize: (v) => v.trim() || undefined,
  })

  // ドラッグ&ドロップ機能用のstate
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // SearchTabbarのref
  const searchTabbarRef = useRef<HTMLDivElement>(null)

  // クエリパラメータに基づいてtagIdsを決定
  const effectiveTagIds = tag
    ? Array.isArray(tag)
      ? tag.map((t) => Number(t))
      : [Number(tag)]
    : []

  // useSearchExhibitionsフックを使用してデータを取得
  const { data, isValidating } = useSearchExhibitions(
    effectiveTagIds,
    genre as string,
    pageIndex,
    orderBy as string,
    filterBy as string,
    keyword as string,
  )

  // タブのアクティブ状態を判定する関数
  const isActive = (type: string, value: string) => {
    if (type === 'orderBy') return orderBy === value
    if (type === 'filterBy') return filterBy === value
    if (type === 'genre') return genre === value
    return false
  }

  // アクティブタブにスクロールする関数
  const scrollToActiveTab = useCallback(() => {
    if (!searchTabbarRef.current) return

    const container = searchTabbarRef.current
    const activeTab = container.querySelector('.active') as HTMLElement

    if (activeTab) {
      const containerWidth = container.offsetWidth
      const activeTabLeft = activeTab.offsetLeft
      const activeTabWidth = activeTab.offsetWidth

      // アクティブタブが中央に来るようにスクロール
      const scrollPosition =
        activeTabLeft - containerWidth / 2 + activeTabWidth / 2

      container.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth',
      })
    }
  }, [])

  // 初期表示時とアクティブタブが変更された時にスクロール
  useEffect(() => {
    const timer = setTimeout(scrollToActiveTab, 100)
    return () => clearTimeout(timer)
  }, [genre, tag, scrollToActiveTab])

  // マウスダウンイベントハンドラー
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!searchTabbarRef.current) return

    setIsDragging(true)
    setStartX(e.pageX - searchTabbarRef.current.offsetLeft)
    setScrollLeft(searchTabbarRef.current.scrollLeft)
  }

  // マウスリーブイベントハンドラー
  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  // マウスアップイベントハンドラー
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // クリックイベントハンドラー（ドラッグ中は無効化）
  const handleTabClickWithDragCheck = (type: string, value?: string) => {
    if (isDragging) return
    handleTabClick(type, value)
  }

  // マウスムーブイベントハンドラー
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !searchTabbarRef.current) return

    e.preventDefault()
    const x = e.pageX - searchTabbarRef.current.offsetLeft
    const walk = (x - startX) * 2 // スクロール速度を調整
    searchTabbarRef.current.scrollLeft = scrollLeft - walk
  }

  // 現在アクティブなジャンルを取得
  const getActiveGenre = () => {
    if (genre) return genre as string
    if (tag) {
      const selectedTag = masterData?.exhibitionTags?.find(
        (t) => t.value === tag,
      )
      return selectedTag?.category
    }
    return null // デフォルトは最初のジャンル
  }

  const genreCategories = [
    { name: '経営戦略・事業開発' },
    { name: 'ブランディング・マーケティング' },
    { name: 'セールス強化・売上拡大' },
    { name: '人材採用' },
    { name: '人・組織づくり' },
    { name: '業務改善・システム開発・デジタル化' },
    { name: '財務・資金管理・バックオフィス' },
    { name: 'その他' },
  ]

  // マスターデータから、選択したジャンルに属するタグをフィルタリング
  const getFilteredTags = () => {
    const activeGenre = getActiveGenre()

    // 新着一覧、お気に入り、注目のサービスの場合はタグボタンを表示しない
    if (
      !activeGenre ||
      activeGenre === 'recent' ||
      activeGenre === 'recommend' ||
      activeGenre === 'favorite'
    ) {
      return []
    }

    // マスターデータから、選択したジャンルに属するタグをフィルタリング
    if (masterData?.exhibitionTags) {
      return masterData.exhibitionTags.filter(
        (tag) => tag.category === activeGenre,
      )
    }

    return []
  }

  const handleTabClick = (type: string, value?: string) => {
    const newQuery = { ...router.query }

    if (type === 'genre') {
      newQuery.genre = value
      // 他のクエリパラメータをクリア
      delete newQuery.tag
      delete newQuery.orderBy
      delete newQuery.filterBy
      // タグ選択はURL変更後にフックが自動でリセット
    } else if (type === 'tag') {
      newQuery.tag = value
      // ジャンルはクリアしない（タグ選択時はジャンルを保持）
      delete newQuery.genre
      delete newQuery.orderBy
      delete newQuery.filterBy
    } else if (type === 'orderBy') {
      newQuery.orderBy = value
      delete newQuery.genre
      delete newQuery.tag
      delete newQuery.filterBy
      // タグ選択はURL変更後にフックが自動でリセット
    } else if (type === 'filterBy') {
      newQuery.filterBy = value
      delete newQuery.genre
      delete newQuery.tag
      delete newQuery.orderBy
      // タグ選択はURL変更後にフックが自動でリセット
    }

    // ページを1にリセットして検索実行
    newQuery.page = '1'
    setPageIndex(1)

    router.push({
      pathname: router.pathname,
      query: newQuery,
    })
  }

  const handleTagToggle = (tagValue: string) => {
    const newSelectedTags = selectedTags.includes(tagValue)
      ? selectedTags.filter((t) => t !== tagValue)
      : [...selectedTags, tagValue]

    setSelectedTags(newSelectedTags)
  }

  const renderSearchConditions = () => {
    return (
      <ExhibitionTop>
        <h2>検索条件</h2>
        <SearchTabbar
          ref={searchTabbarRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          $isDragging={isDragging}
        >
          <SearchTabbarItem
            className={isActive('filterBy', 'recommend') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('filterBy', 'recommend')}
          >
            注目のサービス
          </SearchTabbarItem>
          <SearchTabbarItem
            className={isActive('orderBy', 'recent') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('orderBy', 'recent')}
          >
            新着サービス
          </SearchTabbarItem>
          <SearchTabbarItem
            className={isActive('filterBy', 'favorite') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('filterBy', 'favorite')}
          >
            お気に入り一覧
          </SearchTabbarItem>
          {genreCategories.map((genreCategory) => (
            <SearchTabbarItem
              key={genreCategory.name}
              className={
                getActiveGenre() === genreCategory.name ? 'active' : ''
              }
              onClick={() =>
                handleTabClickWithDragCheck('genre', genreCategory.name)
              }
            >
              {genreCategory.name}
            </SearchTabbarItem>
          ))}
        </SearchTabbar>
        <SearchConditions>
          {getFilteredTags().length > 0 && (
            <SearchConditionTags>
              {getFilteredTags().map((tag) => (
                <SearchConditionTag
                  key={tag.value}
                  className={selectedTags.includes(tag.value) ? 'selected' : ''}
                  onClick={() => handleTagToggle(tag.value)}
                >
                  {tag.label}
                </SearchConditionTag>
              ))}
            </SearchConditionTags>
          )}
          <SearchKeywordInput>
            <input
              type="text"
              placeholder="キーワードを入れて検索"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
            />
            <SearchIcon />
          </SearchKeywordInput>
          <SearchConditionsDetail>
            <div>詳しい条件：</div>
            <span>
              {[
                isActive('filterBy', 'recommend') && '注目のサービス',
                isActive('orderBy', 'recent') && '新着サービス',
                isActive('filterBy', 'favorite') && 'お気に入り一覧',
                genre && genre,
                selectedTags.length > 0 &&
                  selectedTags
                    .map(
                      (tagValue) =>
                        masterData?.exhibitionTags?.find(
                          (t) => t.value === tagValue,
                        )?.label,
                    )
                    .join(', '),
                keyword && `キーワード: ${keyword}`,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </SearchConditionsDetail>
        </SearchConditions>
      </ExhibitionTop>
    )
  }

  // APIから取得した展示会データをそのまま使用
  const filteredExhibitions = data?.exhibitions || []

  // フィルタリングされた展示会の総数
  const filteredTotalCount = data?.exhibitionsTotal || 0

  // データが取得できない場合はローディング状態
  if (isValidating || !data)
    return (
      <Layout>
        {renderSearchConditions()}
        <ExhibitionNoResult />
      </Layout>
    )

  const perPage = 15 // 1ページあたりの件数（固定）

  return (
    <>
      <Layout>
        {renderSearchConditions()}

        {filteredExhibitions.length > 0 && (
          <ExhibitionResult>
            <ExhibitionSummary>
              {filteredTotalCount}件中{(pageIndex - 1) * perPage + 1}～
              {Math.min(pageIndex * perPage, filteredTotalCount)}件表示中
            </ExhibitionSummary>
            <CardsGrid>
              {filteredExhibitions.map((e) => (
                <ExhibitionCard
                  key={e.id}
                  exhibition={e}
                  cardHeight={360}
                  cardWidth={280}
                  imageHeight={153}
                  imageWidth={272}
                  loggedIn={!!user}
                />
              ))}
            </CardsGrid>
            <Pagination
              itemSize={filteredTotalCount}
              setIndex={(pageIndex) => {
                setPageIndex(pageIndex)
                router.push({
                  pathname: router.pathname,
                  query: { ...router.query, page: pageIndex },
                })
              }}
              forcePage={pageIndex - 1}
              onePageDisplayItems={perPage}
            />
          </ExhibitionResult>
        )}
        {filteredExhibitions.length === 0 && (
          <ExhibitionNoResult>検索結果はありませんでした</ExhibitionNoResult>
        )}
      </Layout>
    </>
  )
}

const Layout = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 64px;
`
const ExhibitionTop = styled.div`
  display: flex;
  align-items: left;
  flex-direction: column;
  gap: 24px;
  h2 {
    font-size: 20px;
    font-weight: ${fontWeightBold};
  }
`
const SearchTabbar = styled.div<{ $isDragging: boolean }>`
  display: flex;
  overflow-x: auto;
  scroll-behavior: ${({ $isDragging }) => ($isDragging ? 'auto' : 'smooth')};
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  user-select: none;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &:active {
    cursor: grabbing;
  }
`
const SearchTabbarItem = styled.div`
  font-size: 13px;
  font-weight: ${fontWeightBold};
  padding: 16px 24px;
  color: #a8a9ac;
  border-bottom: 2px solid #cdcdcd;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  user-select: none;
  flex-shrink: 0;

  &:hover {
    color: #11171f;
    border-color: ${accentColor};
  }

  &.active {
    color: #11171f;
    border-color: ${accentColor};
  }
`
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 32px;
`
const SearchConditions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`
const SearchConditionTags = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`
const SearchConditionTag = styled.div`
  font-size: 16px;
  font-weight: ${fontWeightBold};
  padding: 12px 16px;
  background: transparent;
  border-radius: 100px;
  border: 1px solid #e4e4e4;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #effaf6;
    color: ${accentColor};
    border-color: ${accentColor};
  }

  &.selected {
    background: #effaf6;
    color: ${accentColor};
    border-color: ${accentColor};
  }
`
const SearchKeywordInput = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #e4e4e4;
    border-radius: 5px;
    font-size: 16px;
    outline: none;

    &:focus {
      border-color: ${accentColor};
    }

    &::placeholder {
      color: #a8a9ac;
    }
  }

  svg {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #a8a9ac;
  }
`
const SearchConditionsDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  div {
    font-size: 14px;
    font-weight: ${fontWeightBold};
  }
  span {
    display: block;
    font-size: 14px;
  }
`
const ExhibitionResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
const ExhibitionSummary = styled.div`
  font-size: 14px;
  font-weight: ${fontWeightBold};
  color: #393e4c;
`
const ExhibitionNoResult = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
  font-size: 20px;
  font-weight: ${fontWeightBold};
  text-align: center;
  border-radius: 0 64px 0 64px;
  background: #f8f8f8;
`
